import {
  fieldCropHasDisplayableInfo,
  formatFieldCropDateLabel,
  pickPrimaryFarmingPractice,
  type FieldCropInformation,
} from '../../../config/digisakaApi';
import { useTheme } from '../theme/ThemeContext';
import { getAnalysisPanelTheme } from '../theme/themeStyles';

type FieldCropInfoCardProps = {
  info: FieldCropInformation | null;
  onChangeDetails?: () => void;
};

function plantingMethodMeta(method: string | null | undefined) {
  const normalized = String(method || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .trim();
  if (normalized.includes('transplant')) {
    return { icon: 'ri-seedling-line', label: 'Transplanted' };
  }
  if (normalized.includes('direct') || normalized.includes('seed')) {
    return { icon: 'ri-plant-line', label: 'Direct seeded' };
  }
  if (normalized.includes('broadcast')) {
    return { icon: 'ri-scatter-chart-line', label: 'Broadcast' };
  }
  return method ? { icon: 'ri-plant-line', label: method } : null;
}

function statusClass(status: string | null | undefined, isDark: boolean) {
  const key = String(status || '').toLowerCase();
  if (key === 'ongoing') {
    return isDark
      ? 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/20'
      : 'bg-emerald-50 text-emerald-800 ring-emerald-200';
  }
  if (key === 'completed' || key === 'harvested') {
    return isDark
      ? 'bg-gray-700/80 text-gray-300 ring-white/10'
      : 'bg-gray-100 text-gray-700 ring-gray-200';
  }
  return isDark
    ? 'bg-amber-500/15 text-amber-200 ring-amber-400/20'
    : 'bg-amber-50 text-amber-800 ring-amber-200';
}

export default function FieldCropInfoCard({ info, onChangeDetails }: FieldCropInfoCardProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);

  if (!fieldCropHasDisplayableInfo(info) || !info) return null;

  const practice = pickPrimaryFarmingPractice(info);
  const crop = practice?.crop_type || info.crop_type || 'Crop';
  const method = plantingMethodMeta(practice?.planting_method);
  const status = practice?.status || null;
  const season = practice?.cropping_season || null;
  const sowing = practice?.sowing_date || info.sowing_date;
  const planting = practice?.planting_date || null;
  const harvest = practice?.expected_harvest_date || null;
  const area =
    typeof info.total_land_area === 'number' && Number.isFinite(info.total_land_area)
      ? `${info.total_land_area} ha`
      : null;

  const milestones = [
    { key: 'sow', label: 'Sown', value: sowing },
    ...(planting ? [{ key: 'plant', label: 'Planted', value: planting }] : []),
    { key: 'harvest', label: 'Harvest', value: harvest },
  ];

  return (
    <div className={`overflow-hidden rounded-2xl border ${ap.cardSolid} ${ap.accentBorder}`}>
      <div
        className={`flex items-start justify-between gap-3 px-4 py-3.5 ${
          isDark
            ? 'bg-gradient-to-r from-emerald-950/50 to-transparent'
            : 'bg-gradient-to-r from-[#00854014] to-transparent'
        }`}
      >
        <div className="min-w-0">
          <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${ap.accentText}`}>
            Field crop
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h4 className={`truncate text-base font-semibold tracking-tight ${ap.heading}`}>{crop}</h4>
            {status && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${statusClass(
                  status,
                  isDark
                )}`}
              >
                {status}
              </span>
            )}
          </div>
          <p className={`mt-0.5 truncate text-xs ${ap.muted}`}>
            {[info.name, area].filter(Boolean).join(' · ') || 'Saved farm record'}
          </p>
        </div>
        {onChangeDetails && (
          <button
            type="button"
            onClick={onChangeDetails}
            className={`shrink-0 rounded-lg px-2.5 py-1 text-[11px] font-medium transition-colors active:scale-[0.97] ${
              isDark
                ? 'text-emerald-300 hover:bg-white/5'
                : 'text-[#008540] hover:bg-[#00854014]'
            }`}
          >
            Change
          </button>
        )}
      </div>

      <div className={`grid gap-2 px-4 py-3 ${milestones.length > 2 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {milestones.map((item, index) => (
          <div key={item.key} className="min-w-0">
            <div className={`text-[10px] font-medium uppercase tracking-wider ${ap.faint}`}>{item.label}</div>
            <div className={`mt-0.5 truncate text-sm font-medium ${ap.heading}`}>
              {formatFieldCropDateLabel(item.value)}
            </div>
            {index < milestones.length - 1 && (
              <div className="sr-only">then</div>
            )}
          </div>
        ))}
      </div>

      {(method || season) && (
        <div className={`flex flex-wrap gap-1.5 border-t px-4 py-2.5 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
          {method && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                isDark ? 'bg-white/5 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <i className={`${method.icon} text-xs ${ap.accentText}`} />
              {method.label}
            </span>
          )}
          {season && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
                isDark ? 'bg-white/5 text-gray-200' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {season} season
            </span>
          )}
        </div>
      )}
    </div>
  );
}
